import { CircularProgress, Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useState } from 'react';
import { useDebounce } from 'use-debounce';

import { useSelectedTenant, useSetTenantHandleMutation } from '@/hooks/useSwr';
import { getTenantHandleValidationError } from '@/utils/hub';
import { cn } from '@/utils/tailwind';

export default function SetHandle({ onSuccess }: { onSuccess: () => void }) {
  const [handle, setHandle] = useState('');
  const {
    trigger: saveHandle,
    isMutating,
    error: saveError,
  } = useSetTenantHandleMutation();
  const onSubmit = async (e) => {
    e.preventDefault();
    const res = await saveHandle({ json: { tenant_handle: handle } });
    if (res !== undefined) {
      onSuccess();
    }
  };
  const [handleHelpError] = useDebounce(
    getTenantHandleValidationError(handle),
    300
  );
  const error = handleHelpError || saveError;
  const currentTenant = useSelectedTenant();
  const disabled = !!handleHelpError || isMutating || handle.length === 0;

  return (
    <form onSubmit={onSubmit} className="flex w-[480px] flex-col gap-5 p-6">
      <div className="flex flex-col gap-2 text-center">
        <div className="text-lg font-semibold leading-loose">
          {`Choose ${
            currentTenant?.data?.is_personal
              ? 'your public handle'
              : 'a public handle for your workspace'
          }`}
        </div>
        <div className="text-sm font-normal text-ls-black dark:text-secondary">
          Public prompts are discoverable in the LangChain Hub and require an
          owner handle. Once chosen, it's here to stay.
        </div>
      </div>
      <div className="flex w-full flex-col gap-1.5">
        <input
          className="h-10 w-full rounded-md border border-secondary bg-transparent px-3 py-2 text-sm font-normal leading-tight placeholder:text-zinc-500 focus:border-brand focus:outline-none dark:placeholder:text-zinc-400"
          placeholder="pollytheparrot"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
        />
        <div
          className={cn(
            'text-sm font-normal leading-tight',
            error ? 'text-error' : 'text-stone-500 dark:text-stone-400'
          )}
        >
          {error
            ? error.message
            : 'lowercase alphanumeric and hyphen, starting with a-z'}
        </div>
      </div>
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand-green-500 px-4 disabled:border-none disabled:bg-zinc-500 disabled:opacity-20"
      >
        <div className="text-sm font-medium leading-tight text-white">
          {isMutating ? <CircularProgress color="neutral" /> : 'Create handle'}
        </div>
      </button>
    </form>
  );
}

export const SetHandleModal = ({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog>
        <ModalClose size="lg" />
        <SetHandle
          onSuccess={() => {
            onSuccess && onSuccess();
            onClose();
          }}
        />
      </ModalDialog>
    </Modal>
  );
};
